/**
 * @fileoverview Hook that derives contact role options from host-local lookup data.
 * @module ui-admin/hooks/useContactRoles
 *
 * Responsibilities:
 * - Read active contact-role values and return them in stable display order.
 * - Preserve the domain vocabulary as a fallback when no local values exist.
 *
 * Dependencies: React, CONTACT_ROLES, LookupDataSourceContext, shared lookup contracts.
 * Architecture: Explicit reader wins over the host context; no storage access lives here.
 * Notes: Context values come from the host's Dexie-backed listLookupValues service.
 */
import { useEffect, useState } from 'react';
import { CONTACT_ROLES } from '@firesquirrel/core-domain';
import { useLookupDataSource } from '../contexts/LookupDataSourceContext';
import type { LookupValue } from '../services/lookupService';

/**
 * @contract ContactRoleReader
 * Purpose: Defines the contact-role lookup query expected by this hook.
 * Fields: lookupType argument and Promise of lookup values.
 * Rules: Readers are host-owned and local-first.
 * Sync: Reads the host's local lookup state.
 */
export type ContactRoleReader = (lookupType: string) => Promise<LookupValue[]>;

/**
 * @hook useContactRoles
 * Purpose: Provide active, ordered contact role labels.
 * Returns: string[] sorted by sort order and name.
 * Side Effects: Reads once per reader change through an explicit or context-provided local reader.
 * Offline Behavior: Uses CONTACT_ROLES when no reader exists, loading fails, or the local list is empty.
 */
export function useContactRoles(reader?: ContactRoleReader): string[] {
  const contextReader = useLookupDataSource()?.listLookupValues;
  const activeReader = reader ?? contextReader;
  const [roles, setRoles] = useState<string[]>([...CONTACT_ROLES]);
  useEffect(() => {
    let active = true;
    if (!activeReader) {
      setRoles([...CONTACT_ROLES]);
      return () => { active = false; };
    }
    void activeReader('entityContactRole').then((values) => {
      if (!active) return;
      const names = values
        .filter((value) => value.isActive && !value._deleted)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name))
        .map((value) => value.name);
      setRoles(names.length > 0 ? names : [...CONTACT_ROLES]);
    }).catch(() => {
      if (active) setRoles([...CONTACT_ROLES]);
    });
    return () => { active = false; };
  }, [activeReader]);
  return roles;
}
